/**
 * PURPOSE:
 *   Card aislado para configurar los tabs públicos de deportes individuales en Haylli's.
 *
 * RESPONSIBILITIES:
 *   - Leer config_category.tabs_config_publico para el idevent/idsport actual.
 *   - Permitir seleccionar y ordenar tabs desde una UI segura.
 *   - Persistir el string normalizado que consume la vista pública.
 *   - Permitir volver al default de individuales guardando NULL.
 *
 * COLLABORATORS:
 *   - src/services/configCategoryService.js
 *   - src/utils/publicSportTabsConfig.js
 *   - backend PATCH /configcategory/public-tabs
 *
 * ROUTE:
 *   src/components/Cards/CardConfiguration/CardConfigPublicTabs/CardConfigPublicTabs.jsx
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import {
  getConfigCategory,
  updateConfigCategoryPublicTabs,
} from "../../../../services/configCategoryService.js";
import {
  DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES,
  PUBLIC_SPORT_TABS,
  getPublicSportTabLabel,
  isUsingDefaultIndividualPublicTabs,
  normalizePublicTabCodes,
  resolveIndividualPublicTabCodesForForm,
  serializeTabsConfigPublico,
} from "../../../../utils/publicSportTabsConfig.js";

const ensureDefaultTabInsideSelection = (defaultTab, selectedCodes) => {
  const normalizedDefaultTab = String(defaultTab || "").trim();

  if (selectedCodes.includes(normalizedDefaultTab)) {
    return normalizedDefaultTab;
  }

  return selectedCodes[0] || DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES[0];
};

const CardConfigPublicTabs = () => {
  const { idevent, idsport } = useParams();

  const [configData, setConfigData] = useState(null);
  const [selectedCodes, setSelectedCodes] = useState([
    ...DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES,
  ]);
  const [defaultTabLinkPublico, setDefaultTabLinkPublico] = useState(
    DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES[0]
  );
  const [isDefaultMode, setIsDefaultMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!idevent || !idsport) {
      return;
    }

    setLoading(true);
    try {
      const data = await getConfigCategory({ idevent, idsport });
      const resolvedCodes = resolveIndividualPublicTabCodesForForm(
        data?.tabs_config_publico
      );

      setConfigData(data);
      setSelectedCodes(resolvedCodes);
      setDefaultTabLinkPublico(
        ensureDefaultTabInsideSelection(data?.defaultTabLinkPublico, resolvedCodes)
      );
      setIsDefaultMode(isUsingDefaultIndividualPublicTabs(data?.tabs_config_publico));
    } catch (error) {
      toast.error("No se pudo cargar la configuración de tabs públicos");
      console.error("Error loading public tabs config", error);
    } finally {
      setLoading(false);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isClosed = Number(configData?.state) === 2;
  const isDisabled = loading || saving || isClosed;

  const selectedPreview = useMemo(() => {
    return selectedCodes.map((code, index) => ({
      code,
      order: index + 1,
      label: getPublicSportTabLabel(code),
    }));
  }, [selectedCodes]);

  const serializedPreview = useMemo(() => {
    return serializeTabsConfigPublico(selectedCodes);
  }, [selectedCodes]);

  const updateSelectedCodes = (nextCodes) => {
    const normalizedCodes = normalizePublicTabCodes(nextCodes);
    setSelectedCodes(normalizedCodes);
    setDefaultTabLinkPublico((currentDefault) =>
      ensureDefaultTabInsideSelection(currentDefault, normalizedCodes)
    );
  };

  const handleToggleTab = (code) => {
    if (isDisabled) {
      return;
    }

    setIsDefaultMode(false);
    const codeAsString = String(code);

    if (selectedCodes.includes(codeAsString)) {
      updateSelectedCodes(selectedCodes.filter((item) => item !== codeAsString));
      return;
    }

    updateSelectedCodes([...selectedCodes, codeAsString]);
  };

  const handleMove = (fromIndex, direction) => {
    if (isDisabled) {
      return;
    }

    const toIndex = fromIndex + direction;

    if (toIndex < 0 || toIndex >= selectedCodes.length) {
      return;
    }

    setIsDefaultMode(false);
    const nextCodes = [...selectedCodes];
    const [movedCode] = nextCodes.splice(fromIndex, 1);
    nextCodes.splice(toIndex, 0, movedCode);
    updateSelectedCodes(nextCodes);
  };

  const handleRemove = (code) => {
    if (isDisabled) {
      return;
    }

    setIsDefaultMode(false);
    updateSelectedCodes(selectedCodes.filter((item) => item !== code));
  };

  const handleDefaultTabChange = (event) => {
    setDefaultTabLinkPublico(event.target.value);
  };

  const handleSave = async () => {
    const normalizedCodes = normalizePublicTabCodes(selectedCodes);

    if (!normalizedCodes.length) {
      toast.error("Debes seleccionar al menos un tab público");
      return;
    }

    const nextDefaultTab = ensureDefaultTabInsideSelection(
      defaultTabLinkPublico,
      normalizedCodes
    );

    setSaving(true);
    try {
      await updateConfigCategoryPublicTabs({
        idevent,
        idsport,
        tabs_config_publico: serializeTabsConfigPublico(normalizedCodes),
        defaultTabLinkPublico: nextDefaultTab,
      });

      setSelectedCodes(normalizedCodes);
      setDefaultTabLinkPublico(nextDefaultTab);
      setIsDefaultMode(false);
      toast.success("¡Tabs públicos guardados correctamente!");
    } catch (error) {
      toast.error("No se pudo guardar la configuración de tabs públicos");
      console.error("Error saving public tabs config", error);
    } finally {
      setSaving(false);
    }
  };

  const handleUseDefault = async () => {
    const defaultCodes = [...DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES];

    setSaving(true);
    try {
      await updateConfigCategoryPublicTabs({
        idevent,
        idsport,
        tabs_config_publico: null,
        defaultTabLinkPublico: defaultCodes[0],
      });

      setSelectedCodes(defaultCodes);
      setDefaultTabLinkPublico(defaultCodes[0]);
      setIsDefaultMode(true);
      toast.success("¡Se restauró la configuración por defecto!");
    } catch (error) {
      toast.error("No se pudo restaurar la configuración por defecto");
      console.error("Error resetting public tabs config", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex justify-center bg-gray-100 p-4">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-2xl w-full">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-800">
            Configuración de Tabs Públicos
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Solo aplica a deportes individuales. El orden seleccionado será el
            orden visible en Haylli's.
          </p>
        </div>

        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-600">
            Cargando configuración...
          </div>
        ) : (
          <>
            {isClosed && (
              <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                Esta configuración está cerrada. No se pueden realizar cambios.
              </div>
            )}

            <div className="mb-5 rounded-lg border border-blue-100 bg-blue-50 p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold text-blue-800">
                  Estado actual: {isDefaultMode ? "Por defecto" : "Personalizado"}
                </p>
                <p className="text-xs font-mono text-blue-700">
                  DB: {isDefaultMode ? "NULL" : serializedPreview || "—"}
                </p>
              </div>
              <p className="mt-2 text-xs text-blue-700">
                Si está en modo por defecto se usa: Equipos, Información,
                Programación y Resultados.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
                Tabs disponibles
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PUBLIC_SPORT_TABS.map((tab) => {
                  const isSelected = selectedCodes.includes(tab.code);

                  return (
                    <button
                      key={tab.code}
                      type="button"
                      onClick={() => handleToggleTab(tab.code)}
                      disabled={isDisabled}
                      className={`rounded-xl border p-4 text-left transition duration-200 ${
                        isSelected
                          ? "border-blue-400 bg-blue-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50"
                      } ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            {tab.code}. {tab.label}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {tab.description}
                          </p>
                        </div>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-bold ${
                            isSelected
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {isSelected ? "Activo" : "Agregar"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-6 rounded-xl border border-gray-200 p-4">
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  Orden público final
                </h3>
                <span className="text-xs font-mono text-gray-500">
                  {serializedPreview || "Sin tabs seleccionados"}
                </span>
              </div>

              {selectedPreview.length ? (
                <div className="space-y-2">
                  {selectedPreview.map((tab, index) => (
                    <div
                      key={`${tab.code}-${index}`}
                      className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-semibold text-gray-800">
                          {tab.order}. {tab.label}
                        </p>
                        <p className="text-xs text-gray-500">Código {tab.code}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleMove(index, -1)}
                          disabled={isDisabled || index === 0}
                          className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(index, 1)}
                          disabled={isDisabled || index === selectedPreview.length - 1}
                          className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(tab.code)}
                          disabled={isDisabled}
                          className="rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  Debes seleccionar al menos un tab para guardar.
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Tab inicial público
              </label>
              <select
                value={defaultTabLinkPublico}
                onChange={handleDefaultTabChange}
                disabled={isDisabled || !selectedCodes.length}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                {selectedCodes.map((code) => (
                  <option key={code} value={code}>
                    {code}. {getPublicSportTabLabel(code)}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-gray-500">
                Si el tab inicial no existe dentro de la selección, se ajustará
                automáticamente al primer tab seleccionado.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={handleSave}
                disabled={isDisabled || !selectedCodes.length}
                className="rounded bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Guardando..." : "Guardar configuración"}
              </button>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={loadConfig}
                  disabled={isDisabled}
                  className="rounded bg-gray-200 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancelar cambios
                </button>
                <button
                  type="button"
                  onClick={handleUseDefault}
                  disabled={isDisabled}
                  className="rounded bg-emerald-500 px-4 py-2 font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Usar default
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CardConfigPublicTabs;
